import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { X, Search, Radar, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { useStaleEnrichmentCleanup } from '@/hooks/useStaleEnrichmentCleanup';

interface EnrichmentStats {
  total: number;
  wsDone: number;
  wsActive: number;
  drDone: number;
  drActive: number;
  failedCount: number;
}

interface EnrichmentProgressBarProps {
  companyId: string;
}

export const EnrichmentProgressBar: React.FC<EnrichmentProgressBarProps & { userId?: string }> = ({ companyId, userId }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<EnrichmentStats | null>(null);
  const [dismissed, setDismissed] = useState(false);
  useStaleEnrichmentCleanup(companyId, userId);

  const fetchStats = useCallback(async () => {
    if (!userId) return;

    const base = () => supabase.from('leads').select('id', { count: 'exact', head: true })
      .eq('company_id', companyId).eq('created_by', userId);

    const [totalRes, wsDoneRes, wsActiveRes, drDoneRes, drActiveRes, failedRes] = await Promise.all([
      base(),
      base().eq('web_search_status', 'completed'),
      base().in('web_search_status', ['pending', 'processing', 'retrying']),
      base().eq('deep_research_status', 'completed'),
      base().in('deep_research_status', ['processing', 'scraping', 'extracting']),
      base().or('web_search_status.eq.failed,deep_research_status.eq.failed,crawl_status.eq.failed'),
    ]);

    const total = totalRes.count ?? 0;
    const wsDone = wsDoneRes.count ?? 0;
    const wsActive = wsActiveRes.count ?? 0;
    const drDone = drDoneRes.count ?? 0;
    const drActive = drActiveRes.count ?? 0;
    const failedCount = failedRes.count ?? 0;

    setStats({ total, wsDone, wsActive, drDone, drActive, failedCount });
  }, [companyId, userId]);

  // Poll stats
  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  if (dismissed || !stats || stats.total === 0) return null;

  const wsPercent = stats.total > 0 ? (stats.wsDone / stats.total) * 100 : 0;
  const drPercent = stats.total > 0 ? (stats.drDone / stats.total) * 100 : 0;
  const hasActiveJobs = stats.wsActive > 0 || stats.drActive > 0;
  const hasFailed = stats.failedCount > 0;
  const allDone = stats.wsDone === stats.total && stats.drDone === stats.total;

  if (!hasActiveJobs) return null;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-fade-in">
        <div
          onClick={() => navigate('/enrichment-queue')}
          className="bg-popover/95 backdrop-blur-md border border-border rounded-2xl shadow-lg px-5 py-3 flex items-center gap-4 min-w-[340px] max-w-[520px] cursor-pointer hover:bg-popover transition-colors"
        >
          {hasActiveJobs ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="relative flex h-2.5 w-2.5 flex-shrink-0 cursor-default">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>Enrichment in progress</p>
              </TooltipContent>
            </Tooltip>
          ) : hasFailed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <AlertTriangle className="h-4 w-4 text-destructive flex-shrink-0 cursor-default" />
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>{stats.failedCount} leads have failed enrichment — click to view details</p>
              </TooltipContent>
            </Tooltip>
          ) : null}

          <div className="flex-1 min-w-0">

            <div className="flex gap-3 items-center text-xs text-muted-foreground mb-1.5">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 cursor-default">
                    <Search className="h-3 w-3" />
                    <span>{stats.wsDone}/{stats.total}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Web Search: {stats.wsDone} of {stats.total} leads enriched</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 cursor-default">
                    <Radar className="h-3 w-3" />
                    <span>{stats.drDone}/{stats.total}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Deep Research: {stats.drDone} of {stats.total} leads completed</p>
                </TooltipContent>
              </Tooltip>
            </div>

            <div className="flex flex-col gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden cursor-default">
                    <div
                      className={cn(
                        "h-full rounded-full transition-all duration-500",
                        stats.wsActive > 0 ? "bg-amber-500" : "bg-primary"
                      )}
                      style={{ width: `${wsPercent}%` }}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Web Search progress: {Math.round(wsPercent)}%</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden cursor-default">
                    <div
                      className={cn(
                        "h-full rounded-full transition-all duration-500",
                        stats.drActive > 0 ? "bg-amber-500" : "bg-emerald-500"
                      )}
                      style={{ width: `${drPercent}%` }}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Deep Research progress: {Math.round(drPercent)}%</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          {hasFailed && (
            <span className="text-xs text-destructive font-medium flex-shrink-0">
              {stats.failedCount} failed
            </span>
          )}

          <button
            onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
            className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </TooltipProvider>
  );
};
